<script setup lang="ts">
import { computed } from 'vue'
import { UiButtonVariant } from '@afs/design-system'

import { useSimulatorData } from '~/composables/useSimulatorData'
import { formatNumberToLocale } from '~/composables/useFormatNumberToLocale'
import { useI18n } from 'vue-i18n'

const emit = defineEmits<{
  (e: 'simulator-heading::on-legal-button-click'): void
}>()

const { t } = useI18n()
const { currentStep, data, selectedOfferLabel, simulationResponse } = useSimulatorData()

const editAmount = () => {
  currentStep.value = 'amount'
  simulationResponse.value = null
}

const computedTexts = computed(() => {
  return currentStep.value !== 'proposals'
    ? {
        title: t('simulator.heading.form.title'),
        subtitle: t('simulator.heading.form.subtitle'),
      }
    : {
        title: t('simulator.heading.proposals.title'),
        subtitle: t('simulator.heading.proposals.subtitle', { suboffer: selectedOfferLabel.value }),
      }
})
</script>

<template>
  <div class="SimulatorHeading color-blue-01">
    <div class="SimulatorHeading__text">
      <h1 class="heading-1 mb-2">{{ computedTexts.title }}</h1>
      <p class="paragraph-2">{{ computedTexts.subtitle }}</p>
      <atoms-ui-button
        v-if="simulationResponse"
        class="SimulatorHeading__legal-button mt-3"
        :text="$t('simulator.heading.legalNotice.button')"
        :variant="UiButtonVariant.TEXT"
        @click="emit('simulator-heading::on-legal-button-click')"
      />
    </div>
    <button
      v-if="(currentStep === 'duration' || simulationResponse) && data.amount"
      class="SimulatorHeading__edit pa-4 bg-red-03"
      @click="editAmount"
    >
      <p class="paragraph-3">{{ $t('simulator.header.edit.label') }}</p>
      <p class="SimulatorHeading__edit__amount g-1 paragraph-1">
        <span class="heading-2">{{ formatNumberToLocale(data.amount) }}</span>
        €
        <atoms-ui-icon
          class="SimulatorHeading__edit__icon ml-1"
          name="pen"
        />
      </p>
    </button>
  </div>
</template>

<style lang="scss" scoped>
@use 'sass:map';
@use 'config/boxes';
@use 'config/colors';
@use 'utilities/functions' as func;
@use 'utilities/mq';

.SimulatorHeading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  grid-column: span 4;

  @include mq.media('<medium') {
    flex-direction: column;
    align-items: center;
  }
}

.SimulatorHeading__text {
  text-align: center;

  @include mq.media('>=medium') {
    &:only-child {
      margin: 0 auto;
    }

    &:not(:only-child) {
      text-align: left;
    }
  }
}

.SimulatorHeading__legal-button {
  &.Button--text {
    padding-inline: 0;

    & :deep(.Button__text) {
      text-decoration: underline;
    }
  }
}

.SimulatorHeading__edit {
  border-top-left-radius: map.get(boxes.$radii, 'l');
  border-bottom-right-radius: map.get(boxes.$radii, 'l');
  border-top-right-radius: map.get(boxes.$radii, 's');
  border-bottom-left-radius: map.get(boxes.$radii, 's');
  transition: box-shadow 0.2s func.ease(out-quart);

  @media (hover: hover) {
    &:hover {
      box-shadow: inset 0 0 0 2px map.get(colors.$blue, '01');
    }
  }

  @include mq.media('<medium') {
    align-self: stretch;
  }

  @include mq.media('>=medium') {
    min-width: 270px;
  }
}

.SimulatorHeading__edit__amount {
  display: flex;
  align-items: baseline;
  justify-content: center;

  .Icon {
    align-self: center;
    transform: translateY(-2px);
  }
}

.SimulatorHeading__edit__icon {
  font-size: 2.4rem;
}
</style>
